import { useEffect, useState } from 'react';
import { Plus, Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { formatBRL } from '../../lib/currency';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

interface GiftCard {
  id: string; code: string; amount_brl: number; status: string;
  redeemed_by: string | null; redeemed_at: string | null; created_at: string;
  product_price_id: string | null;
  profiles: { email: string | null } | null;
}

const SAFE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
function genCode(len = 16): string {
  const parts = Array.from({ length: len }, () => SAFE_CHARS[Math.floor(Math.random() * SAFE_CHARS.length)]).join('');
  return [parts.slice(0, 4), parts.slice(4, 8), parts.slice(8, 12), parts.slice(12, 16)].join('-');
}

export function AdminGiftCardsPage() {
  const { profile }    = useAuth();
  const [cards,    setCards]    = useState<GiftCard[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [amount,   setAmount]   = useState('');
  const [priceId,  setPriceId]  = useState('');
  const [qty,      setQty]      = useState('1');
  const [saving,   setSaving]   = useState(false);
  const [prices,   setPrices]   = useState<{ id: string; name: string; price_brl: number }[]>([]);

  useEffect(() => { loadCards(); loadPrices(); }, []);

  async function loadCards() {
    const { data } = await supabase
      .from('gift_cards')
      .select('id, code, amount_brl, status, redeemed_by, redeemed_at, created_at, product_price_id, profiles:redeemed_by(email)')
      .order('created_at', { ascending: false })
      .limit(100);
    setCards((data ?? []) as unknown as GiftCard[]);
    setLoading(false);
  }

  async function loadPrices() {
    const { data } = await supabase.from('product_prices').select('id, name, price_brl').eq('active', true).order('price_brl');
    setPrices(data ?? []);
  }

  async function createCards() {
    if (!amount || parseInt(qty) < 1) return;
    setSaving(true);
    const count = Math.min(parseInt(qty), 50);
    const inserts = Array.from({ length: count }, () => ({
      code:             genCode(),
      amount_brl:       parseFloat(amount),
      product_price_id: priceId || null,
      status:           'active',
      created_by:       profile?.id,
    }));
    await supabase.from('gift_cards').insert(inserts);
    setSaving(false);
    setShowForm(false);
    setAmount(''); setPriceId(''); setQty('1');
    loadCards();
  }

  function exportCsv() {
    const headers = ['Código', 'Valor', 'Status', 'Resgatado por', 'Data resgate', 'Criado em'];
    const rows = cards.map(c => [
      c.code, String(c.amount_brl), c.status,
      c.profiles?.email ?? c.redeemed_by ?? '',
      c.redeemed_at ? new Date(c.redeemed_at).toLocaleDateString('pt-BR') : '',
      new Date(c.created_at).toLocaleDateString('pt-BR'),
    ]);
    const csv = [headers, ...rows].map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `gift_cards_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
  }

  const active   = cards.filter(c => c.status === 'active').length;
  const redeemed = cards.filter(c => c.status === 'redeemed').length;
  const totalVal = cards.filter(c => c.status === 'active').reduce((s, c) => s + (c.amount_brl ?? 0), 0);

  return (
    <div>
      <AdminPageHeader title="Gift Cards" objective="Crie e monitore gift cards (presentes digitais) que permitem presentear alguém com acesso ao Professya. Cada código é de uso único. Códigos não resgatados após longo período representam receita diferida." />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1 className="font-display" style={{ fontSize: '1.5rem', color: 'var(--text-primary)' }}>Gift Cards</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary btn-sm" onClick={exportCsv}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Download size={14} /> Exportar
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Plus size={16} /> Gerar códigos
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {[
          { label: 'Ativos', value: active, color: '#4ade80', tooltip: undefined },
          { label: 'Resgatados', value: redeemed, color: 'var(--text-muted)', tooltip: undefined },
          { label: 'Saldo Ativo', value: formatBRL(totalVal), color: '#F5C842', tooltip: 'Saldo Ativo: valor total dos gift cards ainda não resgatados. Este valor representa uma obrigação de serviço (receita diferida).' },
        ].map(s => (
          <div key={s.label} className="card" style={{ flex: '1 1 140px' }} title={s.tooltip}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 0.3rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</p>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, color: s.color, margin: 0 }}>{s.value}</p>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '1rem' }}>
        Gift cards não expiram, mas recomenda-se comunicar ao presenteado em até 30 dias.
      </p>

      {showForm && (
        <div className="card animate-fade-in" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem' }}>Gerar novos códigos</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Valor (R$)</label>
              <input type="number" className="form-input" value={amount} onChange={e => setAmount(e.target.value)} placeholder="24.90" min="1" step="0.01" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Plano vinculado (opcional)</label>
              <select className="form-select" value={priceId} onChange={e => setPriceId(e.target.value)}>
                <option value="">Qualquer plano</option>
                {prices.map(p => <option key={p.id} value={p.id}>{p.name} · {formatBRL(p.price_brl)}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Quantidade (máx. 50)</label>
              <input type="number" className="form-input" value={qty} onChange={e => setQty(e.target.value)} min="1" max="50" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-primary btn-sm" onClick={createCards} disabled={saving || !amount}>
              {saving ? 'Gerando...' : `Gerar ${qty} código(s)`}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="card shimmer" style={{ height: '300px' }} />
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>Código</th><th>Valor</th><th>Status</th><th>Resgatado por</th><th>Data resgate</th></tr>
            </thead>
            <tbody>
              {cards.map(c => (
                <tr key={c.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: '13px', color: c.status === 'active' ? 'var(--petrol)' : 'var(--text-muted)', letterSpacing: '0.05em' }}>{c.code}</td>
                  <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatBRL(c.amount_brl)}</td>
                  <td>
                    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                      backgroundColor: c.status === 'active' ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.05)',
                      color: c.status === 'active' ? '#4ade80' : 'var(--text-muted)' }}>
                      {c.status === 'active' ? 'Ativo' : 'Resgatado'}
                    </span>
                  </td>
                  <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{c.profiles?.email ?? (c.redeemed_by ? c.redeemed_by.slice(0, 12) + '...' : '-')}</td>
                  <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{c.redeemed_at ? new Date(c.redeemed_at).toLocaleDateString('pt-BR') : '-'}</td>
                </tr>
              ))}
              {cards.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>Nenhum gift card gerado ainda</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
